import type { FormEngineHelpers } from './formEngineHelpers';
import { passportOcrFieldFor } from './formEngineHelpers';
import type { PassportProvenanceTracker } from './passportProvenance';
import { createPassportLoadingOwner } from './passportLoading';

export const PASSPORT_EXTRACTION_FAILED_NOTICE =
  'We could not read details from your passport. Please fill in the fields manually.';
export const PASSPORT_EXTRACTION_EMPTY_NOTICE =
  'We could not read details from your passport photo — please check the image quality or enter the details manually.';

export type PassportExtractionResult = {
  ok: boolean;
  data?: unknown;
  nameSyncProof?: string;
};

export type PassportExtractionFactoryContext = {
  form: unknown;
  setExtractingByField?: (updater: (current: Record<string, boolean>) => Record<string, boolean>) => void;
};

export type PassportExtractionApplied = {
  applied: string[];
  form: unknown;
  mappedData: unknown;
  result: PassportExtractionResult;
  isCurrent: () => boolean;
};

export type PassportExtractionAdapterOptions = {
  appId: string | number;
  stepKey: string;
  helpers: FormEngineHelpers;
  provenance: PassportProvenanceTracker;
  extract: (
    fileUrl: string,
    fileName: string,
    appId: string | number,
    stepKey: string,
  ) => Promise<PassportExtractionResult>;
  onErrorNotice: (notice: string | undefined) => void;
  onApplied?: (result: PassportExtractionApplied) => void;
  /** Current application/step and permission scope, evaluated after awaits. */
  isActive?: () => boolean;
  emit: (event: string, properties?: Record<string, unknown>) => void;
};

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

function readFormValues(form: unknown): Record<string, unknown> {
  if (!isObjectRecord(form) || typeof form.getValues !== 'function') return {};
  const values = form.getValues.call(form);
  return isObjectRecord(values) ? values : {};
}

/** References are host document data, never OCR evidence identifiers. */
function documentTokens(value: unknown): string[] {
  if (Array.isArray(value)) return value.flatMap(documentTokens);
  if (typeof value === 'string' && value) {
    const id = /\/documents\/([^/]+)\/download/.exec(value)?.[1];
    return id ? [value, id] : [value];
  }
  if (!isObjectRecord(value)) return [];
  return ['url', 'downloadUrl', 'documentId', 'id'].flatMap((key) => documentTokens(value[key]));
}

/**
 * Historical 2.45-compatible adapter, retained with its regression tests.
 * The installed-runtime hook now uses passportRepairedAdapter with published
 * 2.46 scopes/events and write-time guards; this is not the active binding.
 */
export function createPassportExtractionAdapter(options: PassportExtractionAdapterOptions) {
  const { appId, stepKey, helpers, provenance, extract, onErrorNotice, onApplied, emit } = options;
  const isActive = options.isActive ?? (() => true);
  return ({ form, setExtractingByField }: PassportExtractionFactoryContext) =>
    async (fileUrl: string, fileName: string, fieldIdentifier: string, fieldName = fieldIdentifier) => {
      if (!isActive()) return;
      const startingDocument = documentTokens(readFormValues(form)[fieldName]);
      const requestedDocument = documentTokens(fileUrl);
      let referenceObserved = startingDocument.some((token) => requestedDocument.includes(token));
      const documentIsCurrent = () => {
        const now = documentTokens(readFormValues(form)[fieldName]);
        // AdvancedFileUpload fires extraction before persisting its reference.
        // Initial emptiness alone is NOT removal. The explicit delete callback
        // invalidates the request even when both snapshots are empty.
        if (now.length === 0) return !referenceObserved && startingDocument.length === 0;
        return now.some((token) => requestedDocument.includes(token));
      };
      const extractionId = provenance.beginExtraction();
      const loading = createPassportLoadingOwner(setExtractingByField, fieldIdentifier);
      let subscription: { unsubscribe: () => void } | (() => void) | void;
      const unsubscribe = () => {
        if (typeof subscription === 'function') subscription();
        else subscription?.unsubscribe();
        subscription = undefined;
      };
      let cleaned = false;
      const cleanup = () => {
        if (cleaned) return;
        cleaned = true;
        unsubscribe();
        loading.clear();
      };
      const unregister = provenance.onExtractionInvalidated(extractionId, cleanup);
      if (!unregister || !provenance.isCurrentExtraction(extractionId)) return;
      try {
        if (isObjectRecord(form) && typeof form.watch === 'function') {
          subscription = form.watch.call(form, (_values: unknown, info?: { name?: string }) => {
            if (!isActive() || !provenance.isCurrentExtraction(extractionId)) return;
            if (info?.name && info.name !== fieldName) return;
            const tokens = documentTokens(readFormValues(form)[fieldName]);
            if (tokens.some((token) => requestedDocument.includes(token))) referenceObserved = true;
            // A named upload write to blank is explicit even when dispatch began
            // empty. An unrelated aggregate snapshot is not removal evidence.
            else if (tokens.length || referenceObserved || info?.name === fieldName) {
              provenance.invalidateExtractions();
            }
          });
          if (!provenance.isCurrentExtraction(extractionId)) {
            unsubscribe();
            return;
          }
        }
        loading.start();
        const result = await extract(fileUrl, fileName, appId, stepKey);
        if (!isActive() || !provenance.isCurrentExtraction(extractionId) || !documentIsCurrent()) {
          emit('passport_extraction_stale', { stepKey, reason: 'superseded' });
          return;
        }
        if (!result.ok) {
          onErrorNotice(PASSPORT_EXTRACTION_FAILED_NOTICE);
          return;
        }

        const data = result.data;
        const dataRecord = isObjectRecord(data) ? data : undefined;
        // Both passport_nationality and passport_country are country_select
        // fields that store alpha-2 values. Prefer each field's own code
        // without changing any other extraction payload semantics.
        const mappedData = dataRecord
          ? {
              ...dataRecord,
              nationality:
                dataRecord.nationalityCode ??
                dataRecord.nationality,
              issuingCountry:
                dataRecord.issuingCountryCode ??
                dataRecord.issuingCountry,
            }
          : data;
        const guarded = isObjectRecord(mappedData)
          ? provenance.guardExtraction(extractionId, mappedData, (key) => passportOcrFieldFor(helpers, key))
          : mappedData;
        if (guarded === null) {
          emit('passport_extraction_stale', { stepKey, reason: 'invalidated' });
          return;
        }
        const applied = helpers.applyPassportExtraction?.(form, guarded) ?? [];
        if (!isActive() || !provenance.isCurrentExtraction(extractionId) || !documentIsCurrent()) return;
        if (applied.length === 0) {
          emit('passport_extraction_empty', { stepKey });
          onErrorNotice(PASSPORT_EXTRACTION_EMPTY_NOTICE);
          return;
        }

        const currentValues = readFormValues(form);
        provenance.recordExtractionWrite(
          extractionId,
          Object.fromEntries(applied.map((field) => [field, currentValues[field]])),
        );
        onErrorNotice(undefined);
        onApplied?.({
          applied,
          form,
          mappedData,
          result,
          isCurrent: () => isActive() && provenance.isCurrentExtraction(extractionId) && documentIsCurrent(),
        });
      } catch {
        if (!isActive() || !provenance.isCurrentExtraction(extractionId) || !documentIsCurrent()) return;
        emit('passport_extraction_failed', { stepKey, reason: 'client_error' });
        onErrorNotice(PASSPORT_EXTRACTION_FAILED_NOTICE);
      } finally {
        // Supersession/disposal already ran this request's idempotent cleanup,
        // before a newer handler claimed its loading store.
        cleanup();
        unregister();
      }
    };
}
